/**
 * Minimal template engine for transformation render templates.
 *
 * Syntax: `{expression}` where expression is:
 *   - A dot-path variable: `self.name`, `params.degrees`
 *   - A function call: `joined_names(self.children)`
 *   - With pipe for filter: `self.name | capitalize`
 *   - Index access: `self.children[0].name`
 *
 * Escaped: `\{` produces literal `{`.
 *
 * The engine does NOT use eval() or new Function().
 * All expressions are resolved via a safe walker that only accesses
 * predefined context objects and built-in functions.
 */

import { formatDurationComposite } from './recipe_renderer/textFormatting.js';

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

export interface TemplateNode {
  id: string;
  name: string;
  description: string | null;
  children: TemplateNode[];
  parents: TemplateNode[];
  /** Quantity prefix to prepend to the node name, e.g. "300 ml d'" or "3 u. de ". */
  quantityPrefix?: string;
  /** What this node primarily represents — lets a template (or the
   * `tools()`/`without_tools()` builtins) tell a pot apart from an
   * ingredient among `self.children`. Absent for a node with neither
   * facet (e.g. a bare intermediate split). */
  kind?: 'ingredient' | 'transformation' | 'tool';
}

export interface TemplateContext {
  self: TemplateNode;
  params: Record<string, unknown>;
  graph: {
    node: (id: string) => TemplateNode | null;
  };
  /** The current transformation usage's own duration, in seconds — null
   * when unset. See format_duration() for turning this into display text. */
  duration: number | null;
}

// ────────────────────────────────────────────────────────────────────────────
// Public API
// ────────────────────────────────────────────────────────────────────────────

/**
 * Evaluate a template string with the given context.
 * Returns the rendered string with all template expressions resolved.
 *
 * @param template - The template string (e.g., `{self.name}: Posa {joined_names(self.children)}`)
 * @param context - The evaluation context with self, params, and graph
 * @returns The rendered string
 */
export function evaluateTemplate(
  template: string,
  context: TemplateContext,
): string {
  if (!template) return '';

  const result: string[] = [];
  let lastIndex = 0;
  const length = template.length;

  for (let i = 0; i < length; i++) {
    // Handle escaped brace: \{ → literal {
    if (template[i] === '\\' && i + 1 < length && template[i + 1] === '{') {
      result.push(template.slice(lastIndex, i));
      result.push('{');
      lastIndex = i + 2;
      i++; // skip the '{'
      continue;
    }

    // Start of an expression block
    if (template[i] === '{') {
      // Find the matching closing brace
      const start = i + 1;
      let depth = 1;
      let j = start;

      while (j < length && depth > 0) {
        if (template[j] === '{') {
          depth++;
        } else if (template[j] === '}') {
          depth--;
        }
        j++;
      }

      if (depth === 0) {
        // Valid expression: template[start..j-2]
        const expression = template.slice(start, j - 1);
        result.push(template.slice(lastIndex, i));
        result.push(evaluateExpression(expression, context));
        lastIndex = j;
        i = j - 1; // will be incremented by the loop
      }
      // If depth > 0, unclosed brace — treated as literal
      // (fall through to continue scanning)
    }
  }

  // Append any remaining literal text
  result.push(template.slice(lastIndex));

  return result.join('');
}

// ────────────────────────────────────────────────────────────────────────────
// Expression Evaluation
// ────────────────────────────────────────────────────────────────────────────

/**
 * Evaluate a single template expression (the content inside `{...}`).
 */
function evaluateExpression(expr: string, context: TemplateContext): string {
  const trimmed = expr.trim();

  try {
    // Split on pipe operator (not inside function parentheses)
    const pipeParts = splitOnPipe(trimmed);
    if (pipeParts.length > 1) {
      // Pipe: left expression passes as first arg to right function
      // e.g., "self.name | capitalize" → "capitalize(self.name)"
      let value = evaluateSimpleExpression(pipeParts[0].trim(), context);

      for (let i = 1; i < pipeParts.length; i++) {
        const fnName = pipeParts[i].trim();
        const fn = builtinFunctions[fnName];
        if (typeof fn === 'function') {
          try {
            value = String(fn(value));
          } catch {
            return `«error in ${fnName}(${value})»`;
          }
        } else {
          return `«${fnName}»`;
        }
      }

      return value;
    }

    return evaluateSimpleExpression(trimmed, context);
  } catch {
    return `«${trimmed}»`;
  }
}

/**
 * Resolve a simple expression (no pipes) and return the raw value.
 * Unlike evaluateSimpleExpression, this preserves the original type
 * (arrays, objects, numbers) instead of converting everything to string.
 * This is used for function argument evaluation.
 */
function resolveSimpleExpression(expr: string, context: TemplateContext): unknown {
  const trimmed = expr.trim();

  // Handle || fallback operator: first non-empty result wins
  const orParts = splitOnDoublePipe(trimmed);
  if (orParts.length > 1) {
    for (const part of orParts) {
      const result = resolveSimpleExpression(part.trim(), context);
      // Skip null/undefined/empty/«...» values
      if (result !== null && result !== undefined && result !== '' &&
          !(typeof result === 'string' && result.startsWith('«'))) {
        return result;
      }
    }
    // All failed — return last result as-is
    return resolveSimpleExpression(orParts[orParts.length - 1].trim(), context);
  }

  // Check if it's a function call: func_name(arg1, arg2, ...)
  const fnMatch = trimmed.match(/^([_a-zA-Z][_a-zA-Z0-9.]*)\((.*)\)$/);
  if (fnMatch) {
    const fnName = fnMatch[1];
    const argsStr = fnMatch[2].trim();

    let fn: unknown;
    if (fnName.includes('.')) {
      // Dotted function name: resolve from context (e.g., graph.node)
      fn = resolveDotPath(fnName, context);
    } else {
      fn = builtinFunctions[fnName];
    }
    if (typeof fn !== 'function') {
      return `«${trimmed}»`;
    }

    // Parse arguments (split by comma, respecting nested parens)
    const args = parseArguments(argsStr);

    // Evaluate each argument using resolveSimpleExpression (preserves types)
    const evaluatedArgs = args.map(arg => {
      const trimmedArg = arg.trim();
      const resolved = resolveSimpleExpression(trimmedArg, context);
      // If resolution failed (fallback string), return raw trimmed arg
      if (typeof resolved === 'string' && resolved.startsWith('«') && resolved.endsWith('»')) {
        // Could be a variable name — try literal
        const dotResolved = resolveDotPath(trimmedArg, context);
        if (dotResolved !== undefined) {
          return dotResolved;
        }
        return trimmedArg;
      }
      return resolved;
    });

    try {
      return fn(...evaluatedArgs);
    } catch {
      return `«error in ${trimmed}»`;
    }
  }

  // If it's a string literal (quoted), return it directly
  if ((trimmed.startsWith('"') && trimmed.endsWith('"')) ||
      (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
    return trimmed.slice(1, -1);
  }

  // Try as dot-path variable access
  return resolveDotPath(trimmed, context);
}

/**
 * Evaluate a simple expression (no pipes) and return the string result.
 * For function calls, this delegates to resolveSimpleExpression and converts the result.
 */
function evaluateSimpleExpression(expr: string, context: TemplateContext): string {
  const trimmed = expr.trim();

  // Handle || fallback operator: first non-empty result wins
  const orParts = splitOnDoublePipe(trimmed);
  if (orParts.length > 1) {
    for (const part of orParts) {
      const result = evaluateSimpleExpression(part.trim(), context);
      if (result && result.trim() && !result.startsWith('«')) {
        return result;
      }
    }
    // All failed — return last result as-is
    return evaluateSimpleExpression(orParts[orParts.length - 1].trim(), context);
  }

  // Check if it's a function call: func_name(arg1, arg2, ...)
  const fnMatch = trimmed.match(/^([_a-zA-Z][_a-zA-Z0-9.]*)\((.*)\)$/);
  if (fnMatch) {
    try {
      const result = resolveSimpleExpression(trimmed, context);
      if (result === null || result === undefined) return '';
      // TemplateNode values render as clickable/hoverable HTML spans
      if (isTemplateNode(result)) {
        return nodeToHtmlLink(result);
      }
      return String(result);
    } catch {
      return `«${trimmed}»`;
    }
  }

  // If it's a string literal (quoted), return it directly
  if ((trimmed.startsWith('"') && trimmed.endsWith('"')) ||
      (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
    return trimmed.slice(1, -1);
  }

  // Try as dot-path variable access
  let result: unknown = resolveDotPath(trimmed, context);

  if (result === undefined || result === null) {
    return `«${trimmed}»`;
  }

  // TemplateNode values render as clickable/hoverable HTML spans
  if (isTemplateNode(result)) {
    return nodeToHtmlLink(result);
  }

  // Arrays render as joined clickable node links (Catalan convention)
  if (Array.isArray(result)) {
    return joined_names(result);
  }

  return String(result);
}

// ────────────────────────────────────────────────────────────────────────────
// Dot-Path Resolution
// ────────────────────────────────────────────────────────────────────────────

/**
 * Resolve a dot-path expression like `self.children[0].name` against the context.
 * Returns undefined if any part cannot be resolved.
 */
function resolveDotPath(path: string, context: TemplateContext): unknown {
  // Split on dots, but handle array index notation
  const segments = path.split('.').filter(Boolean);

  // The first segment identifies the root object
  if (segments.length === 0) return undefined;

  let current: unknown = getRootContext(segments[0], context);
  if (current === undefined) return undefined;

  // Start from second segment (first was the root key)
  for (let i = 1; i < segments.length; i++) {
    const seg = segments[i];
    const { key, index } = parseSegment(seg);

    if (current === null || current === undefined) return undefined;

    if (Array.isArray(current)) {
      // Accessing array property — this is unusual but handle it
      if (key === 'length') {
        current = current.length;
        continue;
      }
      // Try integer index
      const idx = parseInt(key, 10);
      if (!isNaN(idx)) {
        current = current[idx];
        continue;
      }
      // Maybe it's a method or property on arrays?
      return undefined;
    }

    // Normal object property access
    if (typeof current === 'object') {
      current = (current as Record<string, unknown>)[key];

      // If there's an array index, apply it
      if (index !== undefined && Array.isArray(current)) {
        current = current[index];
      }
    } else {
      return undefined;
    }
  }

  return current;
}

/**
 * Parse a segment like `children[0]` into key=`children` and index=`0`.
 */
function parseSegment(segment: string): { key: string; index: number | undefined } {
  const match = segment.match(/^([_a-zA-Z][_a-zA-Z0-9]*)\[(\d+)\]$/);
  if (match) {
    return { key: match[1], index: parseInt(match[2], 10) };
  }
  return { key: segment, index: undefined };
}

/**
 * Get the root context object for the given key.
 */
function getRootContext(key: string, context: TemplateContext): unknown {
  switch (key) {
    case 'self':
      return context.self;
    case 'params':
      return context.params;
    case 'graph':
      return context.graph;
    case 'duration':
      return context.duration;
    default:
      return undefined;
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Argument Parsing
// ────────────────────────────────────────────────────────────────────────────

/**
 * Split a comma-separated argument list, respecting nested parentheses.
 * e.g., `self.children[0], "catalan"` → `["self.children[0]", "\"catalan\""]`
 */
function parseArguments(argsStr: string): string[] {
  const args: string[] = [];
  let current = '';
  let parenDepth = 0;
  let bracketDepth = 0;

  for (let i = 0; i < argsStr.length; i++) {
    const ch = argsStr[i];

    if (ch === '(') {
      parenDepth++;
      current += ch;
    } else if (ch === ')') {
      parenDepth--;
      current += ch;
    } else if (ch === '[') {
      bracketDepth++;
      current += ch;
    } else if (ch === ']') {
      bracketDepth--;
      current += ch;
    } else if (ch === ',' && parenDepth === 0 && bracketDepth === 0) {
      args.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }

  if (current.trim()) {
    args.push(current.trim());
  }

  return args;
}

/**
 * Split expression on `|` pipe operators, but not inside parentheses or brackets.
 */
function splitOnPipe(expr: string): string[] {
  const parts: string[] = [];
  let current = '';
  let parenDepth = 0;
  let bracketDepth = 0;

  for (let i = 0; i < expr.length; i++) {
    const ch = expr[i];

    if (ch === '(') {
      parenDepth++;
      current += ch;
    } else if (ch === ')') {
      parenDepth--;
      current += ch;
    } else if (ch === '[') {
      bracketDepth++;
      current += ch;
    } else if (ch === ']') {
      bracketDepth--;
      current += ch;
    } else if (ch === '|' && parenDepth === 0 && bracketDepth === 0) {
      parts.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }

  if (current.trim()) {
    parts.push(current.trim());
  }

  return parts;
}

/**
 * Split expression on `||` fallback operator, but not inside parentheses, brackets, or string literals.
 */
function splitOnDoublePipe(expr: string): string[] {
  const parts: string[] = [];
  let current = '';
  let parenDepth = 0;
  let bracketDepth = 0;
  let inString: string | null = null;

  for (let i = 0; i < expr.length; i++) {
    const ch = expr[i];

    // Track string literals so || inside quotes is ignored
    if (inString) {
      current += ch;
      if (ch === '\\') {
        i++; // skip escaped char
        if (i < expr.length) current += expr[i];
      } else if (ch === inString) {
        inString = null;
      }
      continue;
    }

    if (ch === '"' || ch === "'") {
      inString = ch;
      current += ch;
      continue;
    }

    if (ch === '(') {
      parenDepth++;
      current += ch;
    } else if (ch === ')') {
      parenDepth--;
      current += ch;
    } else if (ch === '[') {
      bracketDepth++;
      current += ch;
    } else if (ch === ']') {
      bracketDepth--;
      current += ch;
    } else if (ch === '|' && i + 1 < expr.length && expr[i + 1] === '|' && parenDepth === 0 && bracketDepth === 0) {
      parts.push(current.trim());
      current = '';
      i++; // skip second |
    } else {
      current += ch;
    }
  }

  if (current.trim()) {
    parts.push(current.trim());
  }

  return parts;
}

// ────────────────────────────────────────────────────────────────────────────
// Built-in Functions
// ────────────────────────────────────────────────────────────────────────────

/**
 * Render a single node as a clickable/hoverable HTML span.
 */
function nodeToHtmlLink(node: TemplateNode): string {
  const text = node.quantityPrefix ? `${node.quantityPrefix}${node.name}` : node.name;
  return `<span class="preparation-ingredient" data-node-id="${escapeHtml(node.id)}">${escapeHtml(text)}</span>`;
}

/**
 * Render an unknown value as an HTML string.
 * TemplateNode objects become clickable spans;
 * plain strings/numbers are HTML-escaped.
 */
function valueToHtml(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (isTemplateNode(value)) {
    return nodeToHtmlLink(value);
  }
  return escapeHtml(String(value));
}

/**
 * Type guard: check if a value looks like a TemplateNode (has id, name).
 */
function isTemplateNode(value: unknown): value is TemplateNode {
  return (
    value !== null &&
    typeof value === 'object' &&
    'id' in (value as Record<string, unknown>) &&
    'name' in (value as Record<string, unknown>)
  );
}

/**
 * HTML-escape a string.
 */
function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Joins node names with Catalan convention, producing clickable HTML spans:
 * - Single node → clickable span
 * - Two nodes → `<a> i <b>`
 * - Three+ → `<a>, <b> i <c>`
 */
export function joined_names(nodes: unknown[]): string {
  if (!Array.isArray(nodes) || nodes.length === 0) return '';

  const links = nodes.map(n => valueToHtml(n));

  if (links.length === 1) return links[0];
  if (links.length === 2) return `${links[0]} i ${links[1]}`;

  const last = links[links.length - 1];
  const rest = links.slice(0, -1).join(', ');
  return `${rest} i ${last}`;
}

/**
 * Uppercases first character of a string.
 */
export function capitalize(s: string): string {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Lowercases a string.
 */
export function lower(s: string): string {
  if (!s) return '';
  return s.toLowerCase();
}

/**
 * Uppercases a string.
 */
export function upper(s: string): string {
  if (!s) return '';
  return s.toUpperCase();
}

/**
 * Format a node's quantity and render as a clickable span.
 * The output looks like: `<span class="preparation-ingredient" data-node-id="...">300 ml d'aigua</span>`
 *
 * Since TemplateNode doesn't carry RecipeNode-level data (ingredientInfo),
 * this function currently renders the node name as a clickable link.
 * Quantity-aware formatting is available when the context is enhanced.
 */
export function format_quantity(node: unknown, _locale?: string): string {
  if (isTemplateNode(node)) {
    // Render as clickable link with the node name
    return nodeToHtmlLink(node);
  }
  if (node === null || node === undefined) return '';
  return escapeHtml(String(node));
}

/**
 * Format a duration (seconds) as composite display text — "1 hora i 30
 * minuts". Delegates to the same formatter used by the hardcoded step
 * text (RecipeTextRenderer.ts's "durant X" phrase), so a custom
 * render_template referencing `{format_duration(duration)}` matches it.
 */
export function format_duration(seconds: unknown): string {
  if (typeof seconds !== 'number') return '';
  return formatDurationComposite(seconds);
}

/**
 * Filter an array of nodes, excluding the one with the given ID.
 * Returns the remaining nodes joined as a Catalan list (same as `joined_names`).
 *
 * Example: `{without(self.children, params.main)}` → "X, Y i Z"
 */
export function without(nodes: unknown[], nodeId: string): string {
  if (!Array.isArray(nodes)) return '';
  const filtered = nodes.filter(n => {
    if (isTemplateNode(n)) {
      return n.id !== nodeId;
    }
    return true;
  });
  return joined_names(filtered);
}

/**
 * Filter an array of nodes, returning only the one with the given ID.
 * Renders as a single clickable span, or empty string if not found.
 *
 * Example: `{only(self.children, params.main)}` → clickable `<span>` for that node
 */
export function only(nodes: unknown[], nodeId: string): string {
  if (!Array.isArray(nodes)) return '';
  const found = nodes.find(n => isTemplateNode(n) && n.id === nodeId);
  if (!found) return '';
  return valueToHtml(found);
}

/**
 * Filter an array of nodes down to the tool-kind ones (e.g. a pot, a
 * handheld blender), joined the same Catalan way as `joined_names`.
 *
 * Example: `{tools(self.children)}` → "l'olla i la batedora de mà"
 */
export function tools(nodes: unknown[]): string {
  if (!Array.isArray(nodes)) return '';
  return joined_names(nodes.filter(n => isTemplateNode(n) && n.kind === 'tool'));
}

// ────────────────────────────────────────────────────────────────────────────
// Built-in functions registry
// ────────────────────────────────────────────────────────────────────────────

export const builtinFunctions: Record<string, Function> = {
  joined_names,
  capitalize,
  lower,
  upper,
  format_quantity,
  format_duration,
  without,
  only,
  tools,
};
